import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Download, LogOut, Search, Users, CalendarCheck, MailCheck, ShieldCheck, FileSpreadsheet } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, formatApiErrorDetail } from "@/lib/api";
import { KEVALTEK_LOGO } from "@/data";

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-sm border border-white/10 bg-card p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon className="h-6 w-6 text-orange" />
      <div className="mt-6 font-display text-4xl font-semibold text-white">{value}</div>
      <div className="mt-1 overline text-muted-foreground">{label}</div>
    </div>
  );
}

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/login", { email, password });
      onSuccess();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-6" data-testid="admin-login">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,87,34,0.12),transparent_55%)]" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-md rounded-sm border border-white/10 bg-card p-10"
      >
        <img src={KEVALTEK_LOGO} alt="KevalTek Expo" className="mb-8 h-12 w-auto" />
        <span className="overline text-orange">Admin Access</span>
        <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight text-white">Dashboard Login</h1>
        <form onSubmit={submit} className="mt-8 flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <Label htmlFor="admin-email" className="text-sm text-steel">Email</Label>
            <Input id="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@kevaltek.com" data-testid="admin-email-input"
              className="h-12 rounded-sm border-white/15 bg-secondary text-white focus-visible:ring-orange focus-visible:ring-offset-0" />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="admin-password" className="text-sm text-steel">Password</Label>
            <Input id="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" data-testid="admin-password-input"
              className="h-12 rounded-sm border-white/15 bg-secondary text-white focus-visible:ring-orange focus-visible:ring-offset-0" />
          </div>
          {error && <p className="font-mono-tech text-xs text-destructive" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={loading}
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-sm bg-orange px-6 py-4 font-mono-tech text-sm font-medium uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
            data-testid="admin-login-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter Dashboard"}
          </button>
        </form>
        <a href="/" className="mt-6 inline-block font-mono-tech text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-orange">← Back to site</a>
      </motion.div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, consented: 0, opted_out: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/admin/registrations"),
        api.get("/admin/stats"),
      ]);
      setRows(r.data.registrations || []);
      setStats(s.data);
    } catch {
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportData = async (format) => {
    setExporting(true);
    try {
      const path = format === "xlsx"
        ? "/admin/registrations/export.xlsx"
        : "/admin/registrations/export";
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `kevaltek_registrations.${format === "xlsx" ? "xlsx" : "csv"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(format === "xlsx" ? "Excel file downloaded." : "CSV downloaded.");
    } catch {
      toast.error("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    onLogout();
  };

  const filtered = rows.filter((r) => {
    const t = `${r.company_name} ${r.visitor_name} ${r.email} ${r.city}`.toLowerCase();
    return t.includes(q.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-4 md:px-10">
          <div className="flex items-center gap-3">
            <img src={KEVALTEK_LOGO} alt="KevalTek Expo" className="h-9 w-auto" />
            <span className="hidden font-mono-tech text-xs uppercase tracking-[0.2em] text-muted-foreground sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={() => exportData("xlsx")} disabled={exporting}
              className="inline-flex items-center gap-2 rounded-sm bg-orange px-4 py-2.5 font-mono-tech text-xs uppercase tracking-[0.15em] text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              data-testid="export-excel-btn">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} <span className="hidden sm:inline">Export to Excel</span><span className="sm:hidden">Excel</span>
            </button>
            <button onClick={() => exportData("csv")} disabled={exporting}
              className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2.5 font-mono-tech text-xs uppercase tracking-[0.15em] text-white transition-colors hover:border-orange hover:text-orange disabled:opacity-60"
              data-testid="export-csv-btn">
              <Download className="h-4 w-4" /> CSV
            </button>
            <button onClick={logout}
              className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-4 py-2.5 font-mono-tech text-xs uppercase tracking-[0.15em] text-white transition-colors hover:border-orange hover:text-orange"
              data-testid="admin-logout-btn">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 py-10 md:px-10">
        <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h1 className="font-display text-4xl font-semibold tracking-tighter text-white">Registrations</h1>
            <p className="mt-2 font-sans text-sm text-muted-foreground">
              {stats.total} total {stats.total === 1 ? "visitor" : "visitors"} · download the full database below
            </p>
          </div>
          <button onClick={() => exportData("xlsx")} disabled={exporting}
            className="inline-flex items-center gap-2 rounded-sm bg-orange px-6 py-3.5 font-mono-tech text-xs uppercase tracking-[0.15em] text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            data-testid="export-excel-main-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Download Excel (.xlsx)
          </button>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={Users} label="Total Visitors" value={stats.total} />
          <StatCard icon={CalendarCheck} label="Today" value={stats.today} />
          <StatCard icon={MailCheck} label="Opted In" value={stats.consented} />
          <StatCard icon={ShieldCheck} label="Opted Out" value={stats.opted_out} />
        </div>

        <div className="mb-5 flex items-center gap-3 rounded-sm border border-white/10 bg-card px-4">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by company, name, email or city…"
            data-testid="search-input"
            className="h-12 w-full bg-transparent font-sans text-sm text-white placeholder:text-muted-foreground/60 focus:outline-none"
          />
        </div>

        <div className="overflow-hidden rounded-sm border border-white/10 bg-card">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center font-mono-tech text-sm text-muted-foreground" data-testid="empty-state">
              No registrations yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="registrations-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    {["Date", "Company", "Visitor", "Email", "Mobile", "City", "Nature", "Follow-up", "Consent"].map((h) => (
                      <TableHead key={h} className="font-mono-tech text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r, i) => (
                    <TableRow key={i} className="border-white/10 hover:bg-white/5" data-testid={`reg-row-${i}`}>
                      <TableCell className="whitespace-nowrap font-mono-tech text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                      </TableCell>
                      <TableCell className="font-medium text-white">{r.company_name}</TableCell>
                      <TableCell className="text-steel">{r.visitor_name}</TableCell>
                      <TableCell className="text-muted-foreground">{r.email}</TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{r.mobile}</TableCell>
                      <TableCell className="text-muted-foreground">{r.city}</TableCell>
                      <TableCell className="max-w-[160px] text-xs text-muted-foreground">{(r.nature_of_business || []).join(", ")}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.preferred_followup || "—"}</TableCell>
                      <TableCell>
                        {String(r.communication_consent).startsWith("I agree") ? (
                          <span className="rounded-sm bg-orange/15 px-2 py-1 font-mono-tech text-[10px] uppercase text-orange">Opted in</span>
                        ) : (
                          <span className="rounded-sm bg-white/5 px-2 py-1 font-mono-tech text-[10px] uppercase text-muted-foreground">No</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking

  const check = useCallback(async () => {
    try {
      await api.get("/auth/me");
      setAuthed(true);
    } catch {
      setAuthed(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink">
        <Loader2 className="h-6 w-6 animate-spin text-orange" />
      </div>
    );
  }
  return authed ? <Dashboard onLogout={() => setAuthed(false)} /> : <Login onSuccess={() => setAuthed(true)} />;
}
